import { cn } from '../../utils'
import { useEnrichedBilling } from '../../hooks/useEnrichedBilling'
import { useBillingCreditConsumption } from '../../hooks'
import { calculateTotalInitialCredits, calculateTotalCredits } from '../../utils/billingUtils'
import { splitCreditBlocks } from '../../utils/billingUtils'
import { YellowWarningIcon } from '../icons/WarningIcons'

export interface ConsumptionBlockProps {
  /**
   * Optional className for styling
   */
  className?: string
}

/**
 * ConsumptionBlock component displays usage or time-based consumption
 * with different visual states based on percentage thresholds
 * Handles all subscription logic internally
 */
// Format percentage for display
const formatPercentage = (value: number): string => {
  if (value === 0) return '0%'
  // Show "<1%" for values greater than 0 but less than 1
  if (value > 0 && value < 1) return '<1%'
  return `${Math.round(value)}%`
}

// Get color for a metric based on its name
const getMetricColor = (metricName: string): string => {
  const name = metricName.toLowerCase()
  if (name.includes('audio note') || name.includes('meeting')) {
    return 'bg-green'
  }
  if (name.includes('token') || name.includes('chat')) {
    return 'bg-teal/80'
  }
  // Fallback colors for other metrics
  return 'bg-transparent'
}

interface UsageSegment {
  name: string
  percentage: number
  credits: number
  color: string
}

export const ConsumptionBlock: React.FC<ConsumptionBlockProps> = ({ className }) => {
  // Get all billing data internally - use paywallState for consistent percentage calculation
  const { subscription, daysRemaining, isFree, billingProfile, paywallState } = useEnrichedBilling()
  const { data: creditConsumption, isLoading: isBillingCreditConsumptionLoading } = useBillingCreditConsumption()

  // Use the same percentage calculation as SidebarUsageBlock via paywallState
  // This ensures both components show the same overall usage percentage
  const overallPercentage = paywallState.credits.percentage
  const consumedCredits = paywallState.credits.used
  const totalAvailableCredits = paywallState.credits.used + paywallState.credits.remaining

  // Monthly (expiring) and additional (non-expiring) credits shown separately.
  const { monthly: monthlyCreditBlocks, added: additionalCreditBlocks } = splitCreditBlocks(
    billingProfile?.creditBlocks ?? [],
  )
  const monthlyCreditsAllocation = calculateTotalInitialCredits(monthlyCreditBlocks)
  const remainingAdditionalCredits = calculateTotalCredits(additionalCreditBlocks)

  // Determine where monthly credits end (for the divider)
  // Divider position based on monthly allocation vs (monthly + remaining additional)
  // Use monthlyCreditsAllocation + remainingAdditionalCredits for display purposes
  const displayTotalCredits = monthlyCreditsAllocation + remainingAdditionalCredits
  const dividerPercentage = displayTotalCredits > 0 ? (monthlyCreditsAllocation / displayTotalCredits) * 100 : 0
  const showDivider =
    monthlyCreditsAllocation > 0 && remainingAdditionalCredits > 0 && dividerPercentage > 0 && dividerPercentage < 100

  // Hide labels if divider is too close to edges (within ~15% from each edge)
  const EDGE_THRESHOLD = 15
  const showMonthlyCreditsLabel = showDivider && dividerPercentage > EDGE_THRESHOLD
  const showAddedCreditsLabel = showDivider && dividerPercentage < 100 - EDGE_THRESHOLD

  const dividerElements = showDivider ? (
    <>
      <div
        className="bg-foreground/40 pointer-events-none absolute h-[6px] w-px"
        style={{ left: `calc(${dividerPercentage}% - 0.5px)`, top: '-8px' }}
      />
      <div
        className="bg-foreground/40 pointer-events-none absolute h-[6px] w-px"
        style={{ left: `calc(${dividerPercentage}% - 0.5px)`, top: '5px' }}
      />
      {showMonthlyCreditsLabel && (
        <span
          className="text-foreground-placeholder pointer-events-none absolute -m-2 text-sm"
          style={{ right: `calc(${100 - dividerPercentage}% + 32px)`, top: '20px' }}
        >
          Monthly Credits
        </span>
      )}
      {showAddedCreditsLabel && (
        <span
          className="text-foreground-placeholder pointer-events-none absolute -m-2 text-sm"
          style={{ left: `calc(${dividerPercentage}% + 32px)`, top: '20px' }}
        >
          Added Credits
        </span>
      )}
    </>
  ) : null

  // Transform per_price_costs into segments for the progress bar breakdown
  // IMPORTANT: Only use items where price.name exactly matches the aggregate name (not tiers)
  // Tier items have names like "Input Token Tier 1" while aggregates have "Input Tokens"
  const usageSegments: UsageSegment[] = creditConsumption?.per_price_costs
    ? (() => {
        // Filter to only include aggregate items (not tier-specific)
        // Aggregates have price.name === price.item.name (e.g., "Input Tokens" === "Input Tokens")
        // Tiers have price.name like "Input Token Tier 1" but price.item.name is "Input Tokens"
        const aggregateItems = creditConsumption.per_price_costs.filter(
          (item) => item.price.name === item.price.item.name && parseFloat(item.total) > 0,
        )

        // Separate token metrics from other metrics using aggregate items only
        const tokenItems = aggregateItems.filter((item) => {
          const name = item.price.name
          return name === 'Input Tokens' || name === 'Output Tokens'
        })
        const otherItems = aggregateItems.filter((item) => {
          const name = item.price.name
          return name !== 'Input Tokens' && name !== 'Output Tokens'
        })

        // Combine Input + Output Tokens into a single segment
        const combinedTokenCredits = tokenItems.reduce((sum, item) => sum + parseFloat(item.total), 0)
        const tokenSegment: UsageSegment | null =
          combinedTokenCredits > 0
            ? {
                name: 'Input + Output Tokens',
                percentage:
                  totalAvailableCredits > 0
                    ? Math.floor(Math.min((combinedTokenCredits / totalAvailableCredits) * 100, 100))
                    : 0,
                credits: combinedTokenCredits,
                color: getMetricColor('Tokens'),
              }
            : null

        // Map other items to segments
        const otherSegments: UsageSegment[] = otherItems.map((item) => {
          const credits = parseFloat(item.total)
          const percentage = totalAvailableCredits > 0 ? (credits / totalAvailableCredits) * 100 : 0
          return {
            name: item.price.item.name,
            percentage: Math.min(percentage, 100), // Cap at 100%
            credits,
            color: getMetricColor(item.price.item.name),
          }
        })

        // Combine token segment with other segments
        return tokenSegment ? [tokenSegment, ...otherSegments] : otherSegments
      })()
    : []

  // Use the overall percentage from paywallState for consistency
  const effectivePercentage = overallPercentage

  // Find percentages for Meeting Notes and Premium Chat Responses
  // Only use aggregate items (where price.name === price.item.name) to avoid double-counting tiers
  const getMeetingPercentage = (): number => {
    // First check usageSegments (already filtered to aggregates only)
    const segment = usageSegments.find((segment) => segment.name.toLowerCase().includes('audio note'))
    if (segment) return Math.floor(segment.percentage)
    return 0
  }

  const getChatPercentage = (): number => {
    // Premium Chat Responses = Input + Output Tokens (aggregates only, not tiers)
    const segment = usageSegments.find(
      (segment) =>
        segment.name.toLowerCase().includes('token') ||
        segment.name.toLowerCase().includes('input') ||
        segment.name.toLowerCase().includes('output'),
    )
    if (segment) return Math.round(segment.percentage)

    return 0
  }

  const meetingPercentage = getMeetingPercentage()
  const chatPercentage = getChatPercentage()

  // Determine consumption type: prefer usage-based for active subscriptions, time-based for trials approaching expiry
  const consumptionType: 'usage' | 'time' =
    subscription.isTrial && daysRemaining !== null && daysRemaining <= 10 ? 'time' : 'usage'

  // Get consumption data - use the same percentage as paywallState for consistency with SidebarUsageBlock
  const consumptionData = useConsumptionBlockData(
    overallPercentage,
    daysRemaining,
    subscription.isTrial || false,
    consumptionType,
    isFree,
    subscription.cancelled || false,
    subscription.daysUntilCancellation || null,
  )

  // Extract values for component logic
  const percentage = consumptionData.percentage
  const displayText = consumptionData.displayText
  const remainingText = consumptionData.remainingText
  const isTrial = subscription.isTrial || false
  const isCancelled = subscription.cancelled || false
  const daysUntilCancellation = subscription.daysUntilCancellation || null
  // Determine the state based on percentage
  const isEnded = percentage >= 100
  const isCritical = percentage >= 90 && percentage < 100
  const isWarning = percentage >= 80 && percentage < 90

  // Determine colors based on state (for fallback single bar)
  // Always use light color - never red. If we have meeting/chat data, use those colors via segments
  const progressBarColor = 'bg-foreground/60'

  // Determine text color for display text
  const displayTextColor =
    isCancelled || isEnded || isCritical
      ? 'text-warning-80'
      : isWarning
        ? 'text-warning-subtle'
        : 'text-foreground-secondary'

  // Determine title based on plan and state
  const getTitle = () => {
    if (isCancelled && daysUntilCancellation !== null && daysUntilCancellation !== undefined) {
      if (daysUntilCancellation <= 0) {
        return 'Your Pro Plan Has Ended'
      }
      return `${daysUntilCancellation} Day${daysUntilCancellation !== 1 ? 's' : ''} of Pro Remaining`
    }
    if (isFree) {
      if (isEnded) {
        return 'Upgrade to Highlight Pro'
      }
      return 'Free Plan Usage'
    }
    if (isTrial) {
      if (isEnded) {
        return 'Pro Trial Usage'
      }
      return 'Pro Trial Usage'
    }
    // Pro user
    if (isEnded) {
      return 'Credit Limit Reached'
    }
    return 'Pro Plan Usage'
  }

  const title = getTitle()

  // Container styling based on state
  const containerStyles = cn(
    'flex flex-col gap-4 rounded-lg p-6 transition-all',
    'border bg-foreground/[2%]',
    className,
  )

  return (
    <div className={containerStyles}>
      {/* Header with title and icon */}
      <div className="flex items-start justify-between">
        <div className="flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <h3 className="text-foreground-secondary m-0 text-[15px]" style={{ fontWeight: 400 }}>
                {title}
              </h3>
            </div>
            <div className="flex items-center gap-1.5">
              {isWarning && !isEnded && !isCancelled && (
                <span className="text-warning-subtle inline-flex items-center">
                  <YellowWarningIcon size={20} />
                </span>
              )}
              {isCritical && !isEnded && !isCancelled && (
                <span className="text-warning-subtle inline-flex items-center">
                  <YellowWarningIcon size={20} />
                </span>
              )}
              {isEnded &&
                !isCancelled &&
                (displayText.includes("You've used all your") ||
                  displayText.includes("You've reached your credit limit")) && (
                  <span className="text-warning-subtle inline-flex items-center">
                    <YellowWarningIcon size={20} />
                  </span>
                )}
              <p className={cn('text-sm', displayTextColor)}>{displayText}</p>
            </div>
            {remainingAdditionalCredits > 0 && (
              <p className="text-foreground-secondary text-sm">
                Your Monthly Credits refresh to 2000 credits each month while your Added Credits do not expire.
              </p>
            )}
          </div>
          {/* <p
            className={cn(
              'm-0 text-sm',
              isCancelled || isEnded ? 'text-red-80' : isCritical ? 'text-red-80' : 'text-foreground-secondary',
            )}
          >
            {displayText}
          </p> */}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-2 w-full">
        {usageSegments.length > 0 ? (
          <div className="flex gap-3">
            <div className="relative min-h-[48px] flex-1">
              <div className="bg-element-hover flex h-[3px] w-full overflow-hidden rounded-full">
                {usageSegments.map((segment, index) => (
                  <div
                    key={segment.name}
                    className={cn('h-full transition-all duration-500', segment.color)}
                    style={{
                      width: `${segment.percentage}%`,
                      marginLeft: index > 0 ? '1px' : '0',
                    }}
                  />
                ))}
              </div>
              {dividerElements}
            </div>
            <span className="text-foreground text-md shrink-0 self-start font-medium leading-[3px]">
              {formatPercentage(effectivePercentage)}
            </span>
          </div>
        ) : (
          <div className="flex gap-3">
            <div className="relative min-h-[48px] flex-1">
              <div className="bg-element-hover h-[3px] w-full overflow-hidden rounded-full">
                <div
                  className={cn('h-full rounded-full transition-all duration-500', progressBarColor)}
                  style={{ width: `${Math.min(percentage, 100)}%` }}
                />
              </div>
              {dividerElements}
            </div>
            <span className="text-foreground text-md shrink-0 self-start font-medium leading-[3px]">
              {formatPercentage(percentage)}
            </span>
          </div>
        )}
        <div className="mt-0 border-t pt-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="bg-border/10 h-4 w-4 rounded">
                <div className="bg-teal/80 h-full w-full rounded" />
              </div>
              <span className="text-foreground-secondary text-sm">Premium Chat Responses</span>
            </div>
            <span className="text-foreground-placeholder shrink-0 text-[15px] font-normal leading-[20px]">
              {formatPercentage(chatPercentage)}
            </span>
          </div>
        </div>
        <div className="mt-3 border-t pb-2 pt-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="bg-element-hover h-4 w-4 rounded">
                <div className="bg-green h-full w-full rounded" />
              </div>
              <span className="text-foreground-secondary text-sm">Meeting Notes</span>
            </div>
            <span className="text-foreground-placeholder shrink-0 text-[15px] font-normal leading-[20px]">
              {formatPercentage(meetingPercentage)}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Hook to determine consumption block data based on usage data
 */
export const useConsumptionBlockData = (
  usagePercentage: number,
  daysRemaining: number | null,
  isTrial: boolean,
  type: 'usage' | 'time',
  isFree: boolean = false,
  isCancelled: boolean = false,
  daysUntilCancellation: number | null = null,
): {
  percentage: number
  displayText: string
  remainingText?: string
} => {
  // Cancellation takes priority for display
  if (isCancelled && daysUntilCancellation !== null) {
    if (daysUntilCancellation <= 0) {
      return {
        percentage: 100,
        displayText: 'Your Pro plan has been cancelled and benefits have ended',
        remainingText: undefined,
      }
    }

    return {
      percentage: 80, // Show as warning state
      displayText: `Your Pro subscription will cancel in ${daysUntilCancellation} day${daysUntilCancellation !== 1 ? 's' : ''}`,
      remainingText: 'Reactivate to keep your benefits',
    }
  }

  if (type === 'usage') {
    // Free users don't have credits, so don't show usage-based consumption
    if (isFree) {
      return {
        percentage: 0,
        displayText: 'Free plan - upgrade to access premium features',
        remainingText: undefined,
      }
    }

    // Determine plan name for messaging
    const planName = isTrial ? 'Pro trial' : 'total available credits'

    if (usagePercentage >= 100) {
      return {
        percentage: 100,
        displayText: isTrial ? "You've used all your Pro trial benefits" : "You've reached your credit limit",
        remainingText: undefined,
      }
    }

    if (usagePercentage >= 90) {
      return {
        percentage: usagePercentage,
        displayText: `You've used ${Math.round(usagePercentage)}% of your available credits`,
        remainingText: `${Math.round(100 - usagePercentage)}% remaining`,
      }
    }

    if (usagePercentage >= 80) {
      return {
        percentage: usagePercentage,
        displayText: `You have used ${Math.round(usagePercentage)}%  of your
      available credits.`,
        remainingText: `${Math.round(100 - usagePercentage)}% remaining`,
      }
    }

    return {
      percentage: usagePercentage,
      displayText: `You have used ${Math.round(usagePercentage)}% of your available credits`,
      remainingText: `${Math.round(100 - usagePercentage)}% remaining`,
    }
  }

  // Time-based (only relevant for trials)
  if (daysRemaining === null || daysRemaining < 0) {
    const daysAgo = Math.abs(daysRemaining || 0)
    return {
      percentage: 100,
      displayText: `Your Highlight Pro trial has ended ${daysAgo > 0 ? `${daysAgo} days ago` : 'today'}`,
      remainingText: undefined,
    }
  }

  if (daysRemaining <= 3) {
    return {
      percentage: 90,
      displayText: `Your Highlight Pro trial ends in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`,
      remainingText: undefined,
    }
  }

  if (daysRemaining <= 10) {
    return {
      percentage: 80,
      displayText: `Your Highlight Pro trial ends in ${daysRemaining} days`,
      remainingText: undefined,
    }
  }

  return {
    percentage: 50,
    displayText: `Your Highlight Pro trial ends in ${daysRemaining} days`,
    remainingText: undefined,
  }
}
